/**
 * Profile serialisation: the JSON that users version-control and share.
 *
 * The format is versioned and validated on import with messages that name the
 * actual problem. A configurator that silently accepts a malformed profile and
 * then pushes something wrong to a device that moves your cursor is worse than one
 * that refuses.
 */

import { PatternKind, PATTERN_KIND_NAMES } from '../../protocol/constants.ts';
import type { JigStep } from '../../protocol/types.ts';
import { defaultDraft, type Draft } from '../../domain/draft.ts';

export const PROFILE_FORMAT = 'vimousejiggler.profile';
export const PROFILE_VERSION = 1;

export interface ProfileFile {
  format: typeof PROFILE_FORMAT;
  version: number;
  name: string;
  /** ISO 8601. */
  updatedAt: string;
  config: {
    kind: number;
    durationMs: number;
    bootDelayMs: number;
    amplitude: number;
    periodMs: number;
    jitter: number;
    seed: number;
    flags: number;
  };
  steps: JigStep[];
}

export function toProfileFile(draft: Draft, name: string, updatedAt = new Date()): ProfileFile {
  return {
    format: PROFILE_FORMAT,
    version: PROFILE_VERSION,
    name,
    updatedAt: updatedAt.toISOString(),
    config: {
      kind: draft.kind,
      durationMs: draft.durationMs,
      bootDelayMs: draft.bootDelayMs,
      amplitude: draft.amplitude,
      periodMs: draft.periodMs,
      jitter: draft.jitter,
      seed: draft.seed,
      flags: draft.flags,
    },
    steps: draft.steps.map((s) => ({ ...s })),
  };
}

export function serialiseProfile(draft: Draft, name: string): string {
  return `${JSON.stringify(toProfileFile(draft, name), null, 2)}\n`;
}

/* ------------------------------------------------------------------ parse -- */

export class ProfileParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProfileParseError';
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function requireNumber(v: unknown, path: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new ProfileParseError(`${path} must be a finite number, got ${JSON.stringify(v)}`);
  }
  return v;
}

function requireInteger(v: unknown, path: string): number {
  const n = requireNumber(v, path);
  if (!Number.isInteger(n)) {
    throw new ProfileParseError(`${path} must be a whole number, got ${n}`);
  }
  return n;
}

export function parseProfile(text: string): { draft: Draft; name: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    throw new ProfileParseError(`Not valid JSON: ${e instanceof Error ? e.message : String(e)}`);
  }

  if (!isRecord(raw)) {
    throw new ProfileParseError('The profile must be a JSON object.');
  }

  if (raw['format'] !== PROFILE_FORMAT) {
    throw new ProfileParseError(
      `Not a ViMouseJiggler profile (format is ${JSON.stringify(raw['format'])}, expected "${PROFILE_FORMAT}").`,
    );
  }

  const version = requireNumber(raw['version'], 'version');
  if (version > PROFILE_VERSION) {
    throw new ProfileParseError(
      `Profile version ${version} is newer than this app understands (${PROFILE_VERSION}). Update the configurator.`,
    );
  }

  const name = typeof raw['name'] === 'string' && raw['name'].length > 0 ? raw['name'] : 'Imported profile';

  const config = raw['config'];
  if (!isRecord(config)) {
    throw new ProfileParseError('The profile is missing its "config" object.');
  }

  const kind = requireInteger(config['kind'], 'config.kind');
  if (!Object.values(PatternKind).includes(kind as never)) {
    const known = Object.entries(PATTERN_KIND_NAMES).map(([k, v]) => `${k}=${v}`).join(', ');
    throw new ProfileParseError(`config.kind ${kind} is not a known pattern kind (${known}).`);
  }

  const stepsRaw = raw['steps'];
  if (!Array.isArray(stepsRaw)) {
    throw new ProfileParseError('The profile is missing its "steps" array.');
  }

  const steps: JigStep[] = stepsRaw.map((entry, i) => {
    if (!isRecord(entry)) {
      throw new ProfileParseError(`steps[${i}] must be an object.`);
    }
    return {
      dx: requireInteger(entry['dx'], `steps[${i}].dx`),
      dy: requireInteger(entry['dy'], `steps[${i}].dy`),
      wheel: requireInteger(entry['wheel'], `steps[${i}].wheel`),
      buttons: requireInteger(entry['buttons'], `steps[${i}].buttons`),
      delayMs: requireInteger(entry['delayMs'], `steps[${i}].delayMs`),
    };
  });

  const draft: Draft = {
    kind: kind as Draft['kind'],
    durationMs: requireNumber(config['durationMs'], 'config.durationMs'),
    bootDelayMs: requireNumber(config['bootDelayMs'], 'config.bootDelayMs'),
    amplitude: requireNumber(config['amplitude'], 'config.amplitude'),
    periodMs: requireNumber(config['periodMs'], 'config.periodMs'),
    jitter: requireNumber(config['jitter'], 'config.jitter'),
    seed: requireNumber(config['seed'], 'config.seed'),
    flags: requireNumber(config['flags'], 'config.flags'),
    steps,
  };

  // Range checking is draft.ts's job so that imported and hand-edited profiles get
  // exactly the same treatment as the editor.
  return { draft, name };
}

/** Convenience for the "no profile" case in tests and resets. */
export function blankProfile(): Draft {
  return defaultDraft();
}
